import { useEffect, useRef, useState } from 'react';

/** Loads iframe src only when near viewport — same size/look after load. */
export function DeferredIframe({
  src,
  title,
  className = '',
  width = '100%',
  height = 410,
  style,
  fill = false,
  ...rest
}) {
  const ref = useRef(null);
  const [activeSrc, setActiveSrc] = useState('');

  useEffect(() => {
    const node = ref.current;
    if (!node || !src) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setActiveSrc(src);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActiveSrc(src);
          observer.disconnect();
        }
      },
      { rootMargin: '250px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [src]);

  const wrapStyle = fill
    ? { position: 'absolute', inset: 0, width: '100%', height: '100%' }
    : { width, minHeight: height };

  const mediaStyle = fill
    ? { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, ...(style || {}) }
    : style;

  return (
    <div ref={ref} style={wrapStyle}>
      {activeSrc ? (
        <iframe
          className={className}
          src={activeSrc}
          title={title}
          width={fill ? '100%' : width}
          height={fill ? '100%' : height}
          style={mediaStyle}
          loading="lazy"
          {...rest}
        />
      ) : (
        <div
          aria-hidden="true"
          style={
            fill
              ? { position: 'absolute', inset: 0, background: '#0f1419' }
              : { width: '100%', height, background: '#0f1419', borderRadius: 12 }
          }
        />
      )}
    </div>
  );
}

/** Starts video load when near viewport; keeps autoplay/muted behavior. */
export function DeferredVideo({
  src,
  className = '',
  autoPlay = false,
  muted = false,
  loop = false,
  playsInline = true,
  controls = false,
  preload = 'none',
  poster,
  id,
  width,
  height,
  children,
}) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '180px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad || !autoPlay) return;
    const video = ref.current;
    if (video && typeof video.play === 'function') {
      video.play().catch(() => {});
    }
  }, [shouldLoad, autoPlay]);

  return (
    <video
      ref={ref}
      id={id}
      className={className}
      src={shouldLoad ? src : undefined}
      autoPlay={shouldLoad ? autoPlay : false}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      controls={controls}
      preload={shouldLoad ? preload : 'none'}
      poster={poster}
      width={width}
      height={height}
    >
      {children}
    </video>
  );
}
